import { useState, useRef } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Megaphone, MessageCircle, Mail, Paperclip, X, Loader2, Upload } from "lucide-react";

const uid = () => Math.random().toString(36).slice(2);

export default function ComunicazioniBox({ cliente, praticaId, senzaCornice }) {
  const [oggetto, setOggetto] = useState("");
  const [testo, setTesto] = useState("");
  const [files, setFiles] = useState([]);
  const [drag, setDrag] = useState(false);
  const [waNum, setWaNum] = useState("cliente");
  const [emailTo, setEmailTo] = useState("cliente");
  const [emailCustom, setEmailCustom] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  const ref = cliente?.referente;
  const cliTel = cliente?.telefono || "";
  const refTel = ref?.telefono || "";
  const cliMail = cliente?.email || "";
  const refMail = ref?.email || "";
  const refName = ref ? [ref.cognome, ref.nome].filter(Boolean).join(" ") : "";

  const addFiles = (list) => { const a = Array.from(list || []).map((f) => ({ uid: uid(), file: f })); if (a.length) setFiles((p) => [...p, ...a]); };
  const removeFile = (u) => setFiles((p) => p.filter((x) => x.uid !== u));
  const hasMsg = testo.trim() || oggetto.trim();

  const sendWhatsapp = async () => {
    let num = (waNum === "referente" ? refTel : cliTel).replace(/\D/g, "");
    if (!num) return toast.error("Numero non disponibile per la scelta effettuata");
    if (num.length === 10) num = "39" + num; // cellulare IT senza prefisso
    if (!hasMsg) return toast.error("Scrivi una comunicazione");
    let text = [oggetto, testo].filter(Boolean).join("\n\n");
    if (files.length) {
      setBusy(true);
      try {
        const fd = new FormData();
        files.forEach((it) => { fd.append("files", it.file); fd.append("names", it.file.name.replace(/\.[^.]+$/, "")); });
        fd.append("categoria", "Comunicazioni");
        const { data } = await api.post(`/clienti/${cliente.id}/upload`, fd);
        const links = (data.uploaded || []).map((u) => u.link).filter(Boolean);
        if (links.length) text += "\n\nAllegati (Drive):\n" + links.join("\n");
        toast.success("Allegati caricati su Drive");
      } catch { toast.warning("Allegati non caricati: invio solo testo"); }
      finally { setBusy(false); }
    }
    window.open(`https://wa.me/${num}?text=${encodeURIComponent(text)}`, "_blank");
    setFiles([]);
  };

  const sendEmail = async () => {
    const to = emailTo === "custom" ? emailCustom.trim() : (emailTo === "referente" ? refMail : cliMail);
    if (!to) return toast.error("Indica un indirizzo email");
    if (!hasMsg) return toast.error("Scrivi una comunicazione");
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("to", to); fd.append("oggetto", oggetto || "Comunicazione RB Studio"); fd.append("testo", testo);
      files.forEach((it) => fd.append("files", it.file));
      const { data } = await api.post(`/pratiche/${praticaId}/email-draft`, fd);
      toast.success("Bozza email creata in Gmail (da rivedere e inviare manualmente).");
      if (data.draft_link) window.open(data.draft_link, "_blank");
      setFiles([]);
    } catch (e) { toast.error(e?.response?.data?.detail || "Errore creazione bozza"); }
    finally { setBusy(false); }
  };

  const inp = "w-full px-3 py-2 text-sm border border-[#1A2B4A]/20 rounded-sm focus:outline-none focus:border-[#C9A84C]";
  const sel = "px-2.5 py-2 text-sm bg-white border border-[#1A2B4A]/20 rounded-sm focus:outline-none focus:border-[#C9A84C]";

  return (
    <div className={senzaCornice ? "" : "bg-white border border-[#1A2B4A]/10 rounded-sm p-4 mt-6"} data-testid="comunicazioni-box">
      <div className="flex items-center gap-2 mb-1">
        <Megaphone className="w-4 h-4 text-[#C9A84C]" />
        {!senzaCornice && <h3 className="font-serif text-xl text-[#1A2B4A]">Comunicazioni</h3>}
      </div>
      <p className="text-sm text-[#4a5568] mb-4">Prepara una comunicazione per il cliente: scrivi, allega file e invia via WhatsApp o crea una bozza email.</p>

      <input data-testid="com-oggetto" value={oggetto} onChange={(e) => setOggetto(e.target.value)} placeholder="Oggetto (usato nell'email)" className={`${inp} mb-2`} />
      <textarea data-testid="com-testo" rows={4} value={testo} onChange={(e) => setTesto(e.target.value)} placeholder="Scrivi qui la comunicazione…" className={`${inp} resize-none mb-3`} />

      <div data-testid="com-drop" onDragEnter={(e) => e.preventDefault()} onDragOver={(e) => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)}
        onDrop={(e) => { e.preventDefault(); setDrag(false); addFiles(e.dataTransfer.files); }}
        className={`rounded-sm border-2 border-dashed px-3 py-2 text-center transition-colors ${drag ? "border-[#C9A84C] bg-[#faf7ef]" : "border-[#1A2B4A]/20"}`}>
        <p className="text-xs text-[#4a5568]">Trascina qui i file oppure</p>
        <button type="button" data-testid="com-pick" onClick={() => inputRef.current?.click()} className="mt-1 inline-flex items-center gap-1 text-xs bg-[#1A2B4A] text-white px-3 py-1.5 rounded-sm hover:bg-[#22345c]">
          <Upload className="w-3.5 h-3.5" /> Scegli file
        </button>
        <input ref={inputRef} type="file" multiple className="hidden" onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
      </div>
      {files.length > 0 && (
        <ul className="mt-2 space-y-1">
          {files.map((it) => (
            <li key={it.uid} className="flex items-center justify-between text-xs bg-[#f9f8f5] rounded-sm px-2.5 py-1.5">
              <span className="flex items-center gap-1.5 truncate"><Paperclip className="w-3 h-3 text-[#C9A84C]" /> {it.file.name}</span>
              <button data-testid="com-file-remove" onClick={() => removeFile(it.uid)} className="text-[#9b2c2c] hover:text-[#7a2222]"><X className="w-3.5 h-3.5" /></button>
            </li>
          ))}
        </ul>
      )}

      <div className="grid sm:grid-cols-2 gap-4 mt-5">
        <div className="border border-[#1A2B4A]/10 rounded-sm p-3">
          <div className="text-[11px] uppercase tracking-wide text-[#4a5568] mb-2">WhatsApp — numero</div>
          <select data-testid="com-wa-num" value={waNum} onChange={(e) => setWaNum(e.target.value)} className={`${sel} w-full mb-2`}>
            <option value="cliente" disabled={!cliTel}>{`Cliente ${cliTel ? `· ${cliTel}` : "· (nessun numero)"}`}</option>
            <option value="referente" disabled={!refTel}>{`Rif. ${refName || ""} ${refTel ? `· ${refTel}` : "· (nessun numero)"}`}</option>
          </select>
          <button data-testid="com-send-wa" onClick={sendWhatsapp} disabled={busy}
            className="w-full inline-flex items-center justify-center gap-1.5 text-sm bg-[#276749] text-white px-3 py-2 rounded-sm hover:opacity-90 disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <MessageCircle className="w-4 h-4" />} Invia WhatsApp
          </button>
        </div>
        <div className="border border-[#1A2B4A]/10 rounded-sm p-3">
          <div className="text-[11px] uppercase tracking-wide text-[#4a5568] mb-2">Email — destinatario</div>
          <select data-testid="com-email-to" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} className={`${sel} w-full mb-2`}>
            <option value="cliente" disabled={!cliMail}>{`Cliente ${cliMail ? `· ${cliMail}` : "· (nessuna email)"}`}</option>
            <option value="referente" disabled={!refMail}>{`Rif. ${refName || ""} ${refMail ? `· ${refMail}` : "· (nessuna email)"}`}</option>
            <option value="custom">Altro indirizzo…</option>
          </select>
          {emailTo === "custom" && (
            <input data-testid="com-email-custom" value={emailCustom} onChange={(e) => setEmailCustom(e.target.value)} placeholder="nome@dominio.it" className={`${inp} mb-2`} />
          )}
          <button data-testid="com-send-email" onClick={sendEmail} disabled={busy}
            className="w-full inline-flex items-center justify-center gap-1.5 text-sm bg-[#1A2B4A] text-white px-3 py-2 rounded-sm hover:bg-[#22345c] disabled:opacity-50">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Mail className="w-4 h-4" />} Crea bozza email
          </button>
        </div>
      </div>
    </div>
  );
}
